import { forwardRef, useEffect, useRef } from "react";
import { Animated } from "react-native-web";
import { tokens } from "@opendoor/tokens-studio/dist/bricks-next/tokens/Components";
import {
  getTokens,
  styled,
  Text as TamaguiText,
  View
} from "@tamagui/core";
import { getStateTokenValue } from "../../helpers/tokens";
import { createAnchorOrButtonFrame } from "../shared/anchorOrButton";
import { Text } from "../Text";
import { jsx, jsxs } from "react/jsx-runtime";
const { button: buttonTokens } = tokens, sharedFrameConfig = {
  name: "Button",
  cursor: "pointer",
  userSelect: "none",
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  borderWidth: buttonTokens.borderWidth.default,
  animation: "linear",
  pressStyle: {
    scale: getStateTokenValue(buttonTokens.state.pressedScale)
  },
  variants: {
    variant: (variant, {
      props
    }) => ({
      backgroundColor: props.disabled ? buttonTokens.color.background.state.inactive[variant] : buttonTokens.color.background[variant],
      borderColor: props.disabled ? buttonTokens.color.border.state.inactive[variant] : buttonTokens.color.border[variant],
      hoverStyle: props.disabled ? {} : {
        backgroundColor: buttonTokens.color.background.state.hover[variant],
        borderColor: buttonTokens.color.border.state.hover[variant]
      },
      focusVisibleStyle: {
        outlineStyle: "solid",
        outlineWidth: buttonTokens.outlineWidth.state.focus.default,
        outlineOffset: buttonTokens.padding.border.focus,
        outlineColor: buttonTokens.color.border.state.focused[variant]
      }
    }),
    size: (size) => ({
      paddingHorizontal: buttonTokens.padding.leftRight[size],
      paddingVertical: buttonTokens.padding.topBottom[size],
      borderRadius: buttonTokens.cornerRadius[size],
      maxWidth: size in buttonTokens.size.maxWidth ? buttonTokens.size.maxWidth[size].state.default : void 0,
      height: size in buttonTokens.size.height ? buttonTokens.size.height[size] : void 0
    }),
    disabled: {
      true: {
        pointerEvents: "none"
      }
    },
    loading: {
      true: {
        pointerEvents: "none"
      }
    }
  }
}, AnchorFrame = styled(TamaguiText, {
  ...sharedFrameConfig,
  tag: "a",
  accessibilityRole: "link",
  textDecorationLine: "none",
  display: "flex",
  position: "relative"
}), ButtonFrame = styled(View, {
  ...sharedFrameConfig,
  tag: "button",
  accessibilityRole: "button"
}), AnchorOrButtonFrame = createAnchorOrButtonFrame({
  AnchorFrame,
  ButtonFrame
}), ButtonLoadingContainer = styled(View, {
  position: "absolute",
  top: 0,
  bottom: 0,
  right: 0,
  left: 0,
  zIndex: 1,
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  variants: {
    variant: (variant, { props }) => ({
      backgroundColor: "isDisabled" in props && props.isDisabled ? buttonTokens.color.background.state.inactive[variant] : buttonTokens.color.background[variant]
    }),
    frameSize: (size) => ({
      borderRadius: buttonTokens.cornerRadius[size],
      gap: buttonTokens.padding.loadingAnimation[size]
    }),
    // isDisabled prop is only used to determine backgound color
    isDisabled: {
      true: {}
    }
  }
}), ButtonLoadingAnimation = ({
  variant,
  size,
  isDisabled
}) => {
  const colorToken = isDisabled ? buttonTokens.color.content.state.inactive[variant] : buttonTokens.color.content[variant], sizeToken = buttonTokens.size.loadingAnimation[size], borderRadiusToken = buttonTokens.cornerRadius.loadingAnimation.default, colorValue = getTokens().color[colorToken].val, sizeValue = getTokens().size[sizeToken].val, borderRadiusValue = getTokens().radius[borderRadiusToken].val, baseStyles = {
    backgroundColor: colorValue,
    width: sizeValue,
    height: sizeValue,
    borderRadius: borderRadiusValue
  }, dotOne = useRef(new Animated.Value(1)).current, dotTwo = useRef(new Animated.Value(0)).current, dotThree = useRef(new Animated.Value(0)).current, dotFour = useRef(new Animated.Value(0)).current, duration = 300, delay = 50;
  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.parallel([
          Animated.timing(dotOne, { toValue: 0, duration, useNativeDriver: !0 }),
          Animated.timing(dotTwo, { toValue: 1, duration, useNativeDriver: !0 })
        ]),
        Animated.parallel([
          Animated.timing(dotTwo, { toValue: 0, duration, useNativeDriver: !0 }),
          Animated.timing(dotThree, { toValue: 1, duration, useNativeDriver: !0 })
        ]),
        Animated.parallel([
          Animated.timing(dotThree, { toValue: 0, duration, useNativeDriver: !0 }),
          Animated.timing(dotFour, { toValue: 1, duration, useNativeDriver: !0 })
        ]),
        Animated.delay(delay),
        Animated.parallel([
          Animated.timing(dotFour, { toValue: 0, duration, useNativeDriver: !0 }),
          Animated.timing(dotThree, { toValue: 1, duration, useNativeDriver: !0 })
        ]),
        Animated.parallel([
          Animated.timing(dotThree, { toValue: 0, duration, useNativeDriver: !0 }),
          Animated.timing(dotTwo, { toValue: 1, duration, useNativeDriver: !0 })
        ]),
        Animated.parallel([
          Animated.timing(dotTwo, { toValue: 0, duration, useNativeDriver: !0 }),
          Animated.timing(dotOne, { toValue: 1, duration, useNativeDriver: !0 })
        ]),
        Animated.delay(delay)
      ])
    );
    return loop.start(), () => {
      loop.stop();
    };
  }, [dotOne, dotTwo, dotThree, dotFour]);
  const opacityMinValue = Number(variant === "secondary" ? buttonTokens.computedValues.opacity.loadingAnimation.inverse : buttonTokens.computedValues.opacity.loadingAnimation.default), interpolation = { inputRange: [0, 1], outputRange: [opacityMinValue, 1] };
  return /* @__PURE__ */ jsxs(
    ButtonLoadingContainer,
    {
      isDisabled,
      variant,
      frameSize: size,
      testID: "button-loading-container",
      children: [
        /* @__PURE__ */ jsx(Animated.View, { style: [baseStyles, { opacity: dotOne.interpolate(interpolation) }] }),
        /* @__PURE__ */ jsx(Animated.View, { style: [baseStyles, { opacity: dotTwo.interpolate(interpolation) }] }),
        /* @__PURE__ */ jsx(Animated.View, { style: [baseStyles, { opacity: dotThree.interpolate(interpolation) }] }),
        /* @__PURE__ */ jsx(Animated.View, { style: [baseStyles, { opacity: dotFour.interpolate(interpolation) }] })
      ]
    }
  );
}, Button = forwardRef(function({ variant, size, disabled, loading, label, ...rest }, ref) {
  return /* @__PURE__ */ jsxs(
    AnchorOrButtonFrame,
    {
      variant,
      size,
      disabled,
      focusable: !disabled,
      loading,
      "aria-disabled": disabled || loading,
      ref,
      ...rest,
      children: [
        loading && /* @__PURE__ */ jsx(ButtonLoadingAnimation, { isDisabled: disabled, variant, size }),
        /* @__PURE__ */ jsx(
          Text,
          {
            tag: "span",
            typography: buttonTokens.typography[size],
            color: disabled ? buttonTokens.color.content.state.inactive[variant] : buttonTokens.color.content[variant],
            numberOfLines: 1,
            children: label
          }
        )
      ]
    }
  );
});
export {
  Button,
  ButtonLoadingAnimation
};
